// src/pages/magasin/RecupererDemande.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../Config';

export default function RecupererDemande() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [demande, setDemande] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API_URL}/recuperation/demandes/${id}`, { headers })
      .then(res => {
        const d = res.data.data;
        setDemande(d);
        const init = {};
        d.demande_materiels?.forEach(item => {
          const restant = item.quantite_preparee - (item.quantite_recuperee || 0);
          if (restant > 0) {
            init[item.materiel_id] = {
              quantite_recuperee: restant,
              etat: 'bon',
              observations: ''
            };
          }
        });
        setForm(init);
      })
      .catch(() => setError('Demande non trouvée'))
      .finally(() => setLoading(false));
  }, [id]);

  const update = (mid, field, value) => setForm(p => ({
    ...p, [mid]: { ...p[mid], [field]: value }
  }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { materiels: {} };
      Object.entries(form).forEach(([mid, data]) => {
        const q = Number(data.quantite_recuperee);
        if (q > 0) payload.materiels[mid] = {
          quantite_recuperee: q,
          etat: data.etat,
          observations: data.observations || ''
        };
      });

      if (Object.keys(payload.materiels).length === 0) throw new Error('Veuillez saisir au moins une quantité à récupérer');

      await axios.post(`${API_URL}/recuperation/demandes/${id}/enregistrer`, payload, { headers });
      setSuccess(true);
    } catch (e) {
      setError(e.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const getEtatColor = (etat) => {
    switch(etat) {
      case 'bon': return 'bg-green-100 text-green-800';
      case 'abime': return 'bg-yellow-100 text-yellow-800';
      case 'irreparable': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de la demande...</p>
        </div>
      </div>
    );
  }

  if (!demande) {
    return (
      <div className="min-h-screen  p-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-red-700">Demande introuvable</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const aRecuperer = demande.demande_materiels?.filter(i => (i.quantite_preparee || 0) > (i.quantite_recuperee || 0)) || [];

  return (
    <div className="min-h-screen ">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={() => navigate('/magasin')}
                className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Retour
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Récupération de matériel</h1>
              <p className="text-gray-600 mt-1">Demande #{demande.numero || id}</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Statut</div>
              <div className="text-sm font-medium px-3 py-1 bg-green-100 text-green-800 rounded-full">
                Prêt pour récupération
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Informations générales */}
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations de la demande</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-sm text-gray-500 mb-1">Demandeur</div>
              <div className="font-medium text-gray-900">{demande.demandeur?.nom || 'Inconnu'}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-1">Date de préparation</div>
              <div className="font-medium text-gray-900">
                {demande.date_preparation ? new Date(demande.date_preparation).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'Non préparée'}
              </div>
            </div>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-2">Total préparé</div>
            <div className="text-2xl font-bold text-blue-600">
              {demande.demande_materiels?.reduce((sum, item) => sum + (item.quantite_preparee || 0), 0) || 0}
            </div>
            <div className="text-sm text-gray-500 mt-1">Articles</div>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-2">Déjà récupéré</div>
            <div className="text-2xl font-bold text-gray-600">
              {demande.demande_materiels?.reduce((sum, item) => sum + (item.quantite_recuperee || 0), 0) || 0}
            </div>
            <div className="text-sm text-gray-500 mt-1">Articles</div>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-2">À récupérer</div>
            <div className="text-2xl font-bold text-orange-600">
              {demande.demande_materiels?.reduce((sum, item) => {
                const restant = (item.quantite_preparee || 0) - (item.quantite_recuperee || 0);
                return sum + Math.max(0, restant);
              }, 0) || 0}
            </div>
            <div className="text-sm text-gray-500 mt-1">Articles restants</div>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-2">Matériels</div>
            <div className="text-2xl font-bold text-gray-900">{demande.demande_materiels?.length || 0}</div>
            <div className="text-sm text-gray-500 mt-1">Références</div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        {aRecuperer.length === 0 ? (
          <div className="bg-white rounded-lg border shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Récupération complète</h3>
            <p className="text-gray-600">Tous les matériels de cette demande ont déjà été récupérés.</p>
            <button
              onClick={() => navigate('/magasin')}
              className="mt-6 px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retour au tableau de bord
            </button>
          </div>
        ) : (
          <>
            {/* Liste des matériels à récupérer */}
            <div className="bg-white rounded-lg border shadow-sm overflow-hidden mb-8">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Matériels à récupérer</h2>
                <p className="text-sm text-gray-500 mt-1">Renseignez les quantités récupérées et leur état</p>
              </div>
              
              <div className="divide-y divide-gray-200">
                {aRecuperer.map((item) => {
                  const courant = form[item.materiel_id] || { quantite_recuperee: 0, etat: 'bon', observations: '' };
                  const restant = item.quantite_preparee - (item.quantite_recuperee || 0);
                  
                  return (
                    <div key={item.materiel_id} className="p-6 hover: transition-colors">
                      <div className="mb-4">
                        <h3 className="text-lg font-medium text-gray-900">{item.materiel?.nom}</h3>
                        {item.materiel?.reference && (
                          <p className="text-sm text-gray-500">Référence : {item.materiel.reference}</p>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                        <div className="space-y-3">
                          <div className="grid grid-cols-3 gap-4">
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <div className="text-xs text-gray-500 mb-1">Préparé</div>
                              <div className="font-semibold text-blue-700">{item.quantite_preparee || 0}</div>
                            </div>
                            <div className=" p-3 rounded-lg">
                              <div className="text-xs text-gray-500 mb-1">Déjà récupéré</div>
                              <div className="font-semibold text-gray-700">{item.quantite_recuperee || 0}</div>
                            </div>
                            <div className="bg-orange-50 p-3 rounded-lg">
                              <div className="text-xs text-gray-500 mb-1">Restant</div>
                              <div className="font-semibold text-orange-700">{restant}</div>
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Quantité récupérée
                            </label>
                            <div className="flex items-center">
                              <input
                                type="number"
                                min="0"
                                max={restant}
                                value={courant.quantite_recuperee}
                                onChange={(e) => {
                                  let v = parseInt(e.target.value) || 0;
                                  if (v > restant) v = restant;
                                  if (v < 0) v = 0;
                                  update(item.materiel_id, 'quantite_recuperee', v);
                                }}
                                className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <span className="ml-3 text-sm text-gray-500">
                                maximum : {restant}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-3">État du matériel</label>
                            <div className="flex space-x-4">
                              {[
                                { value: 'bon', label: 'Bon', color: 'green' },
                                { value: 'abime', label: 'Abîmé', color: 'yellow' },
                                { value: 'irreparable', label: 'Irréparable', color: 'red' }
                              ].map((option) => (
                                <label key={option.value} className="flex items-center">
                                  <input
                                    type="radio"
                                    name={`etat-${item.materiel_id}`}
                                    value={option.value}
                                    checked={courant.etat === option.value}
                                    onChange={(e) => update(item.materiel_id, 'etat', e.target.value)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Observations
                            </label>
                            <textarea
                              value={courant.observations}
                              onChange={(e) => update(item.materiel_id, 'observations', e.target.value)}
                              rows="2"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Notes sur l'état ou la récupération..."
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* État sélectionné */}
                      {courant.etat && (
                        <div className="flex items-center mt-2">
                          <div className={`px-3 py-1 rounded-full text-xs font-medium ${getEtatColor(courant.etat)}`}>
                            État : {courant.etat === 'bon' ? 'Bon' : courant.etat === 'abime' ? 'Abîmé' : 'Irréparable'}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => navigate('/magasin')}
                className="px-6 py-1.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover: transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`px-6 py-1.5 rounded-lg font-medium transition-colors ${
                  saving
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {saving ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Enregistrement...
                  </div>
                ) : (
                  'Confirmer la récupération'
                )}
              </button>
            </div>

            {/* Note */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <div className="text-sm font-medium text-blue-800">Information</div>
                  <div className="text-sm text-blue-700 mt-1">
                    Vérifiez l'état de chaque matériel lors de la récupération. 
                    Les observations permettent de suivre les éventuels problèmes ou remarques.
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Dialog de succès */}
        {success && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mx-auto mb-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Récupération enregistrée
                </h3>
                <p className="text-gray-600 text-center mb-6">
                  La récupération a été enregistrée avec succès.
                </p>
                <div className="flex justify-center">
                  <button
                    onClick={() => navigate('/magasin')}
                    className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Retour au tableau de bord
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}